import { useEffect, useRef, type ReactNode } from 'react';
import { useScrollLock } from '../../lib/scrollLock.js';
import './ShareAccess.css';

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11): the note-modal shell every Share & Access window uses (Share,
 * Reassign, Who has access). The page scrolls as a whole, the white header sticks to the top with
 * the close X (same as Cancel), the footer sticks to the bottom, and Esc closes it. A message (white
 * card, green or red bar) sits just above the footer's buttons, 24px in from the right edge.
 */
export interface SaMessage {
  id: number;
  text: string;
  error?: boolean;
}

let seq = 0;
export const saMessage = (text: string, error = false): SaMessage => ({ id: ++seq, text, error });

export function SaMessageCard({ msg }: { msg: SaMessage }) {
  return (
    <div className={`acs-msg${msg.error ? ' err' : ''}`} role={msg.error ? 'alert' : 'status'}>
      {msg.text}
    </div>
  );
}

export function SaModal({
  title,
  sub,
  onClose,
  children,
  foot,
  msg,
  onMsgDone,
  label,
  scrollKey,
}: {
  title: string;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Left and right footer groups; the green button goes last on the right. */
  foot: { left?: ReactNode; right: ReactNode };
  msg?: SaMessage | null;
  onMsgDone?: () => void;
  label?: string;
  /** A new value (the Share flow's step) takes the page back to the top. */
  scrollKey?: string | number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useScrollLock(true);
  useEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // a dropdown or the confirm dialog on top takes Esc first
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('.cred-confirm-overlay, .dd-open')) return;
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    overlayRef.current?.scrollTo?.(0, 0);
  }, [scrollKey]);
  const msgId = msg?.id;
  useEffect(() => {
    if (msgId === undefined || !onMsgDone) return;
    const t = window.setTimeout(onMsgDone, 2600);
    return () => window.clearTimeout(t);
    // the timer belongs to this message only
  }, [msgId]);
  return (
    <div className="acs-overlay" ref={overlayRef}>
      <div
        className="acs-modal"
        role="dialog"
        aria-modal="true"
        aria-label={label ?? title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="acs-head">
          <div>
            <h2>{title}</h2>
            {sub ? <div className="acs-sub">{sub}</div> : null}
          </div>
          <button
            type="button"
            className="acs-x"
            aria-label="Close"
            title="Close"
            onClick={onClose}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="acs-body">{children}</div>
        <div className="acs-foot">
          {msg ? <SaMessageCard key={msg.id} msg={msg} /> : null}
          <div className="acs-left">{foot.left}</div>
          <div className="acs-right">{foot.right}</div>
        </div>
      </div>
    </div>
  );
}

export function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('') || '?';
